import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu, Heart, Microscope, Users } from 'lucide-react';

const EmoryUniversity: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Emory University";

    return (
        <div className="min-h-screen bg-white font-sans text-[#002878] selection:bg-[#D2B031] selection:text-[#002878]">
             {/* Header */}
            <nav className="bg-white py-6 px-6 md:px-12 shadow-sm sticky top-0 z-50">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex flex-col">
                         <span className="font-serif text-3xl font-bold text-[#002878] tracking-wide">{schoolName.split(' ')[0].toUpperCase()}</span>
                         <span className="font-sans text-[10px] font-bold text-[#002878] uppercase tracking-[0.3em] ml-1">University</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#002878] text-sm uppercase tracking-widest">
                        <a href="#" className="hover:text-[#D2B031] transition-colors ml-4">Schools</a>
                        <a href="#" className="hover:text-[#D2B031] transition-colors ml-4">Libraries</a>
                        <a href="#" className="hover:text-[#D2B031] transition-colors ml-4">Health</a>
                        <a href="#" className="hover:text-[#D2B031] transition-colors ml-4">Give</a>
                        <Search className="text-[#002878] cursor-pointer hover:text-[#D2B031] ml-8" size={20} />
                    </div>

                     <div className="lg:hidden text-[#002878]">
                        <Menu size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Atlanta / Heart */}
            <header className="relative h-[650px] overflow-hidden">
                <img 
                    src="https://images.unsplash.com/photo-1574958229713-356c9e0cf972?q=80&w=2074&auto=format&fit=crop" 
                    alt="Emory Campus" 
                    className="absolute inset-0 w-full h-full object-cover transform scale-105"
                />
                 <div className="absolute inset-0 bg-gradient-to-r from-[#002878]/90 via-[#002878]/30 to-transparent"></div>

                 <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12">
                     <div className="max-w-xl text-white">
                         <h1 className="text-5xl md:text-7xl font-serif font-bold mb-6 leading-tight">
                             Ambitious for the heart.
                         </h1>
                         <p className="text-xl md:text-2xl mb-10 font-light text-gray-200">
                             {schoolName.split(' ')[0]} is a top-ranked research university recognized for its outstanding liberal arts colleges and graduate schools.
                         </p>
                         <Button className="bg-[#D2B031] hover:bg-[#b5962b] text-[#002878] font-bold px-10 h-14 rounded-full shadow-lg">
                             Explore {schoolName.split(' ')[0]}
                         </Button>
                     </div>
                 </div>
            </header>

            {/* Stats / Impact */}
            <section className="py-24 px-6 md:px-12 bg-[#f9f9f9]">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                     <div>
                         <Heart size={40} className="text-[#D2B031] mx-auto mb-6" />
                         <h3 className="text-2xl font-bold text-[#002878] mb-4">Healthcare Leader</h3>
                         <p className="text-gray-600">Home to {schoolName.split(' ')[0]} Healthcare, the most comprehensive health system in Georgia.</p>
                     </div>
                      <div>
                         <Microscope size={40} className="text-[#D2B031] mx-auto mb-6" />
                         <h3 className="text-2xl font-bold text-[#002878] mb-4">$894M Research</h3>
                         <p className="text-gray-600">In funded research awards, driving discovery and innovation.</p>
                     </div>
                      <div>
                         <Users size={40} className="text-[#D2B031] mx-auto mb-6" />
                         <h3 className="text-2xl font-bold text-[#002878] mb-4">Atlanta Hub</h3>
                         <p className="text-gray-600">Located in a global city rich in culture, history, and opportunity.</p>
                     </div>
                </div>
            </section>

             {/* News Cards */}
             <section className="bg-white py-24 px-6 md:px-12">
                  <div className="max-w-[1200px] mx-auto">
                      <div className="flex justify-between items-end mb-12">
                          <div>
                              <span className="text-[#D2B031] font-bold uppercase tracking-widest text-xs mb-2 block">News Center</span>
                              <h2 className="text-4xl font-serif font-bold text-[#002878]">{schoolName.split(' ')[0]} Headlines</h2>
                          </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                          {[
                              { title: `${schoolName.split(' ')[0]} discovers new pathway to treat viral infections`, img: "https://images.unsplash.com/photo-1542736667-069246bdbc6d?q=80&w=2071&auto=format&fit=crop" },
                              { title: "Creative Writing program hosts Pulitzer Prize winner", img: "https://images.unsplash.com/photo-1455849318743-b2233052fcff?q=80&w=2069&auto=format&fit=crop" },
                              { title: "Student-led initiative tackles food insecurity in Atlanta", img: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=2070&auto=format&fit=crop" }
                          ].map((item, i) => (
                              <div key={i} className="group cursor-pointer">
                                  <div className="aspect-[4/3] mb-6 overflow-hidden rounded-lg shadow-sm">
                                      <img src={item.img} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt={item.title}/>
                                  </div>
                                  <h3 className="text-xl font-bold text-[#002878] leading-tight group-hover:text-[#D2B031] transition-colors">
                                      {item.title}
                                  </h3>
                              </div>
                          ))}
                      </div>
                  </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#002878] text-white py-16 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between gap-12 text-sm">
                     <div className="max-w-xs">
                          <span className="font-serif text-2xl font-bold tracking-wide mb-6 block">{schoolName.split(' ')[0].toUpperCase()}</span>
                          <p className="text-gray-300 mb-6">
                              201 Dowman Drive <br/>
                              Atlanta, Georgia 30322 <br/>
                              404.727.6123
                          </p>
                     </div>

                     <div className="grid grid-cols-2 lg:grid-cols-3 gap-12 font-bold text-gray-300">
                         <ul className="space-y-3">
                             <li><a href="#" className="hover:text-[#D2B031]">Contact</a></li>
                             <li><a href="#" className="hover:text-[#D2B031]">Emergency</a></li>
                             <li><a href="#" className="hover:text-[#D2B031]">Careers</a></li>
                         </ul>
                         <ul className="space-y-3">
                             <li><a href="#" className="hover:text-[#D2B031]">{schoolName.split(' ')[0]} News</a></li>
                             <li><a href="#" className="hover:text-[#D2B031]">Events</a></li>
                             <li><a href="#" className="hover:text-[#D2B031]">Give to {schoolName.split(' ')[0]}</a></li>
                         </ul>
                          <ul className="space-y-3">
                             <li><a href="#" className="hover:text-[#D2B031]">Privacy Policy</a></li>
                             <li><a href="#" className="hover:text-[#D2B031]">Copyright</a></li>
                             <li><a href="#" className="hover:text-[#D2B031]">EO/AA Statement</a></li>
                         </ul>
                     </div>
                 </div>
                 <div className="max-w-[1200px] mx-auto mt-16 text-center text-xs text-gray-400 border-t border-[#D2B031]/30 pt-8">
                     © 2026 {schoolName}
                 </div>
             </footer>
        </div>
    );
};

export default EmoryUniversity;
